// @ts-nocheck
import React, { useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useTranslation } from "../i18n/LanguageContext";

const styles = {
  success: {
    Icon: CheckCircleIcon,
    icon: "bg-green-50 text-green-700 ring-green-100",
    accent: "bg-green-500",
    titleKey: "feedback.successTitle",
  },
  error: {
    Icon: ExclamationTriangleIcon,
    icon: "bg-red-50 text-red-700 ring-red-100",
    accent: "bg-red-500",
    titleKey: "feedback.errorTitle",
  },
  info: {
    Icon: InformationCircleIcon,
    icon: "bg-blue-50 text-blue-700 ring-blue-100",
    accent: "bg-blue-500",
    titleKey: "feedback.infoTitle",
  },
};

export default function FeedbackToast({
  type = "info",
  message,
  subtitle,
  title,
  duration = 5000,
  onClose,
}) {
  const { t } = useTranslation();
  const [show, setShow] = useState(Boolean(message));
  const config = styles[type] || styles.info;
  const Icon = config.Icon;

  useEffect(() => {
    setShow(Boolean(message));
    if (!message || duration === 0) return undefined;

    const timeout = window.setTimeout(() => setShow(false), duration);
    return () => window.clearTimeout(timeout);
  }, [message, subtitle, duration]);

  const close = () => {
    setShow(false);
  };

  if (!message) return null;

  return (
    <div className="pointer-events-none fixed inset-x-4 top-5 z-[100] flex justify-end sm:inset-x-6">
      <Transition
        show={show}
        appear
        enter="transform transition duration-300 ease-out"
        enterFrom="translate-y-[-12px] opacity-0 sm:translate-x-6 sm:translate-y-0"
        enterTo="translate-x-0 translate-y-0 opacity-100"
        leave="transform transition duration-200 ease-in"
        leaveFrom="translate-x-0 opacity-100"
        leaveTo="translate-y-[-8px] opacity-0 sm:translate-x-6 sm:translate-y-0"
        afterLeave={onClose}
      >
        <div
          role={type === "error" ? "alert" : "status"}
          aria-live={type === "error" ? "assertive" : "polite"}
          className="pointer-events-auto relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl ring-1 ring-black/5"
        >
          <div className={`absolute inset-y-0 left-0 w-1 ${config.accent}`} />
          <div className="flex items-start gap-3 p-4 pl-5">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1 ${config.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1 pt-0.5">
              <p className="text-sm font-black text-gray-950">{title || t(config.titleKey)}</p>
              <p className="mt-1 break-words text-sm leading-5 text-gray-600">{message}</p>
              {subtitle && <p className="mt-1 text-xs leading-5 text-gray-500">{subtitle}</p>}
            </div>
            <button
              type="button"
              onClick={close}
              aria-label={t('feedback.close')}
              className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-cl"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </Transition>
    </div>
  );
}
